import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input
} from '@angular/core';
import { ZuiClaudeMarkComponent } from '@zerops/zui/claude-mark';
import { ZuiCodexMarkComponent } from '@zerops/zui/codex-mark';
import { ZuiAntigravityMarkComponent } from '@zerops/zui/antigravity-mark';
import { ZuiGrokMarkComponent } from '@zerops/zui/grok-mark';
import { ZuiCursorMarkComponent } from '@zerops/zui/cursor-mark';
import { ZuiSeparatorComponent } from '@zerops/zui/separator'; // for the horizontal timeline connector
import {
  AGENT_DISPLAY_NAMES,
  ZagentAgentType
} from '@zerops/zerops/core/zerops-services';

/**
 * Per-agent state in the strip. The auth dialog walker assigns one of
 * these to each queued agent.
 */
export type AgentStripStatus =
  | 'pending'  // queue not reached yet — faded + greyscale
  | 'active'   // in flight — full color, bold name, accent underline
  | 'done'     // succeeded — full color + green check below logo
  | 'failed'   // errored, user chose not to retry — full color + red x below logo
  | 'skipped'; // user skipped — greyscale + dash below logo, struck-through name

export interface AgentStripEntry {
  agentType: ZagentAgentType;
  status: AgentStripStatus;
}

/**
 * Horizontal row of agent brand-marks with per-agent status. Replaces the
 * single-brand-mark slot at the top of the auth dialog's left column when
 * the dialog is processing more than one agent.
 *
 * Layout per item (vertically stacked):
 *   [ logo  32×32 ]
 *   [ status row  14px high — never empty; pending uses a neutral hairline ]
 *   [ label       ]
 *
 * Status row stays fixed-height across all states so items align across
 * the row and labels sit on the same baseline regardless of which agent
 * happens to be active. Indicators sit OUTSIDE the logo so they don't
 * occlude the brand mark (earlier version had the done-check overlapping
 * the logo bottom-right corner).
 *
 * Brand-mark resolution uses an exhaustive `@switch` over `agentType` —
 * adding a new agent forces the switch to grow (compile-time visible).
 */
@Component({
  selector: 'zui-agent-strip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ZuiClaudeMarkComponent,
    ZuiCodexMarkComponent,
    ZuiAntigravityMarkComponent,
    ZuiGrokMarkComponent,
    ZuiCursorMarkComponent,
    ZuiSeparatorComponent
  ],
  template: `
    <div class="__strip" role="list" [attr.aria-label]="ariaLabel()">
      @for (entry of entries(); track entry.agentType; let last = $last) {
        <div
          class="__item"
          role="listitem"
          [attr.aria-current]="entry.status === 'active' ? 'step' : null"
          [class.__item--active]="entry.status === 'active'"
          [class.__item--done]="entry.status === 'done'"
          [class.__item--failed]="entry.status === 'failed'"
          [class.__item--skipped]="entry.status === 'skipped'"
          [class.__item--pending]="entry.status === 'pending'">

          <span class="__mark" aria-hidden="true">
            @switch (entry.agentType) {
              @case ('claude-code') { <zui-claude-mark /> }
              @case ('codex')       { <zui-codex-mark /> }
              @case ('antigravity') { <zui-antigravity-mark /> }
              @case ('grok')        { <zui-grok-mark /> }
              @case ('cursor')      { <zui-cursor-mark /> }
            }
          </span>

          <span class="__label">{{ nameOf(entry.agentType) }}</span>
        </div>

        @if (!last) {
          <zui-separator
            class="__connector"
            orientation="horizontal"
            size="medium"
            spacing="disabled" />
        }
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
    }

    .__strip {
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      // Shift the whole strip left so the first agent's mark center
      // visually lands at the column's left edge — aligns Claude's
      // logo with the stepper's dot column below.
      margin-left: -21px;
    }

    .__item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 6px;
      min-width: 56px;
      flex: 0 0 auto;
      transition: opacity 0.2s ease;

      // Lift mark + label above the bokeh pseudo so the gradient sits
      // behind them without needing a negative z-index.
      > * {
        position: relative;
        z-index: 1;
      }
    }

    // Bokeh glow behind the whole item (mark + label) — soft radial color
    // cloud reaching well beyond the item bounds, no defined edge. Every
    // non-pending state gets a cloud; the hue carries the meaning
    // (green=done, blue=active, red=failed, grey=skipped).
    .__item--done::after,
    .__item--active::after,
    .__item--failed::after,
    .__item--skipped::after {
      content: '';
      position: absolute;
      inset: -22px;
      border-radius: 50%;
      pointer-events: none;
      opacity: 0.5;
    }

    .__item--done::after {
      background: radial-gradient(circle, rgba(76, 175, 80, 0.3) 0%, rgba(76, 175, 80, 0) 65%);
    }

    .__item--active::after {
      background: radial-gradient(circle, rgba(33, 150, 243, 0.32) 0%, rgba(33, 150, 243, 0) 65%);
      animation: __item-active-pulse 2.4s ease-in-out infinite;
    }

    .__item--failed::after {
      background: radial-gradient(circle, rgba(244, 67, 54, 0.28) 0%, rgba(244, 67, 54, 0) 65%);
    }

    .__item--skipped::after {
      background: radial-gradient(circle, rgba(0, 0, 0, 0.14) 0%, rgba(0, 0, 0, 0) 65%);
    }

    @keyframes __item-active-pulse {
      0%, 100% { opacity: 0.7; transform: scale(1);    }
      50%      { opacity: 1;   transform: scale(1.08); }
    }

    .__item--pending {
      opacity: 0.45;
      filter: grayscale(1);
    }

    .__item--skipped {
      opacity: 0.7;
      filter: grayscale(1);

      .__label {
        text-decoration: line-through;
      }
    }

    // Horizontal connector between items — the timeline rail. Uses
    // zui-separator (size=medium → fixed width). align-self: flex-start
    // + margin-top puts it on the mark vertical midline (mark is 32px
    // tall → +16px lands a 2px line on its center).
    //
    // ng-deep overrides the separator default near-invisible
    // opacity (0.075 in zef-toolkit) up to a hairline-readable level.
    .__connector {
      align-self: flex-start;
      margin-top: 15px;

      ::ng-deep .__sep {
        opacity: 0.18;
      }
    }

    .__mark {
      width: 32px;
      height: 32px;
      font-size: 28px;
      line-height: 1;
      color: rgba(0, 0, 0, 0.85);
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .__label {
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
      letter-spacing: 0.01em;
      color: rgba(0, 0, 0, 0.7);
      max-width: 84px;
      text-align: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .__item--active .__label {
      color: rgba(0, 0, 0, 0.95);
      font-weight: 600;
    }

    .__item--done .__label {
      color: rgba(0, 0, 0, 0.78);
    }

    // Dark mode
    :host-context(.c-theme--dark),
    :host-context(html.zef-dark-theme) {
      .__connector ::ng-deep .__sep {
        background: #fff;
        opacity: 0.25;
      }

      .__mark {
        color: rgba(255, 255, 255, 0.92);
      }

      .__label {
        color: rgba(255, 255, 255, 0.7);
      }

      .__item--active .__label {
        color: rgba(255, 255, 255, 0.98);
      }

      .__item--done .__label {
        color: rgba(255, 255, 255, 0.85);
      }

      .__item--done::after {
        background: radial-gradient(circle, rgba(129, 199, 132, 0.4) 0%, rgba(129, 199, 132, 0) 65%);
      }

      .__item--active::after {
        background: radial-gradient(circle, rgba(100, 181, 246, 0.4) 0%, rgba(100, 181, 246, 0) 65%);
      }

      .__item--failed::after {
        background: radial-gradient(circle, rgba(239, 83, 80, 0.36) 0%, rgba(239, 83, 80, 0) 65%);
      }

      .__item--skipped::after {
        background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0) 65%);
      }
    }
  `
})
export class ZuiAgentStripComponent {
  // # Inputs
  entries = input.required<AgentStripEntry[]>();

  // # Computed
  ariaLabel = computed(() => {
    const total = this.entries().length;
    return total > 1
      ? `Authorization progress: ${total} agents`
      : 'Authorization progress';
  });

  // Template helper — exhaustive `Record` lookup so missing names fail at compile time
  nameOf(t: ZagentAgentType): string {
    return AGENT_DISPLAY_NAMES[t];
  }
}
